import api from './config';

// 获取表情包列表
export const getEmotionPackages = async () => {
  return api.get('/packs');
};

/** 已出现在 packs.category 中的分类（与默认「日常」「限时」在页面侧合并） */
export const getPackCategories = async () => {
  return api.get('/packs/categories');
};

// 获取单个表情包详情
export const getEmotionPackage = async (emotionId) => {
  return api.get(`/packs/${encodeURIComponent(emotionId)}`);
};

// 上传表情包原图（可选生成设备流）
export const uploadEmotionPackage = async (
  file,
  name,
  version,
  description,
  content,
  category,
  hot,
  coverImage,
  previewImages,
  boardTypes,
  imageGenerationModel = 'epd_packbits_rle_v1',
  epdParams = null
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  formData.append('version', version);
  if (description) {
    formData.append('description', description);
  }
  if (content) {
    formData.append('content', content);
  }
  if (category) {
    formData.append('category', category);
  }
  if (hot) {
    formData.append('hot', hot.toString());
  }
  if (coverImage) {
    formData.append('cover_image', coverImage);
  }
  if (previewImages) {
    // 如果是数组，转换为逗号分隔的字符串
    const previewImagesStr = Array.isArray(previewImages) ? previewImages.join(',') : previewImages;
    formData.append('preview_images', previewImagesStr);
  }
  if (boardTypes) {
    // 如果是数组，转换为逗号分隔的字符串
    const boardTypesStr = Array.isArray(boardTypes) ? boardTypes.join(',') : boardTypes;
    formData.append('board_types', boardTypesStr);
  }
  formData.append('image_generation_model', imageGenerationModel || 'epd_packbits_rle_v1');
  if (imageGenerationModel === 'epd_packbits_rle_v1' && epdParams) {
    if (epdParams.mode != null && epdParams.mode !== '') {
      formData.append('epd_mode', String(epdParams.mode));
    }
    if (epdParams.saturate != null) formData.append('epd_saturate', String(epdParams.saturate));
    if (epdParams.contrast != null) formData.append('epd_contrast', String(epdParams.contrast));
    if (epdParams.hue != null) formData.append('epd_hue', String(epdParams.hue));
    if (epdParams.sharpen != null) formData.append('epd_sharpen', String(epdParams.sharpen));
  }
  return api.post('/packs/upload', formData);
};

// 从图片制作 BIN 表情包（多图上传，必须包含 neutral 图片）
export const buildBinFromImages = async (files, name, version, boardTypes) => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('version', version || '1.0.0');
  if (boardTypes != null && boardTypes !== '') {
    const boardTypesStr = Array.isArray(boardTypes) ? boardTypes.join(',') : boardTypes;
    formData.append('board_types', boardTypesStr);
  }
  for (let i = 0; i < files.length; i++) {
    formData.append('file', files[i]);
  }
  return api.post('/packs/build-bin', formData, { timeout: 120000 });
};

// 删除表情包
export const deleteEmotionPackage = async (emotionId) => {
  return api.delete(`/packs/${encodeURIComponent(emotionId)}`);
};

// 下载表情包
export const downloadEmotionPackage = async (emotionId) => {
  return api.get(`/packs/${encodeURIComponent(emotionId)}/download`, { responseType: 'blob' });
};

// 更新表情包信息
export const updateEmotionPackage = async (emotionId, data, isFormData = false) => {
  if (isFormData) {
    return api.put(`/packs/${encodeURIComponent(emotionId)}`, data);
  }
  return api.put(`/packs/${encodeURIComponent(emotionId)}`, data);
};

// 生成下载链接
export const generateDownloadLink = async (emotionId) => {
  return api.post(`/packs/${encodeURIComponent(emotionId)}/generate-link`, {});
};

// 分配表情包到设备（按设备MAC）
export const assignEmotionToDevice = async (emotionId, deviceMac) => {
  return api.post(`/packs/${encodeURIComponent(emotionId)}/assign`, { device_mac: deviceMac });
};

// 取消设备的表情包分配
export const unassignEmotionFromDevice = async (deviceMac) => {
  return api.post('/packs/unassign', { device_mac: deviceMac });
};

// 查询设备当前分配的表情包
export const getAssignedEmotion = async (deviceMac) => {
  return api.get('/packs/assigned', { params: { device_mac: deviceMac } });
};

// 批量分配表情包（支持 device_macs 多行文本和 filters 条件）
export const batchAssignEmotion = async ({ pack_id, device_macs, filters, strategy = 'only-blank', dry_run = false }) => {
  return api.post('/packs/assign/batch', {
    pack_id,
    device_macs,
    filters,
    strategy,
    dry_run
  });
};

// 批量取消分配
export const batchUnassignEmotion = async ({ device_macs, filters, dry_run = false }) => {
  return api.post('/packs/unassign/batch', {
    device_macs,
    filters,
    dry_run
  });
};

// 密钥相关 API 已移除

// 上传表情包图片（封面图或预览图）
export const uploadEmotionImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  // 不要手动设置 Content-Type，让浏览器自动设置（包含 boundary）
  return api.post('/packs/upload-image', formData);
};

// ========== 表情市场接口 ==========
// 获取 API base URL（供兼容旧公开接口使用）
const getApiBaseUrl = () => {
  const rawBase = process.env.VUE_APP_API_BASE_URL || '';
  if (rawBase) {
    return rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;
  }
  return '/api';
};

// 获取表情市场列表（pack 主链路，支持分页/搜索/版型）
export const getEmotionMarket = async ({ page = 1, pageSize = 20, keyword = '', hardwareType = '' } = {}) => {
  // 统一主链路：/api/pack-market/list（支持可选登录）
  const baseUrl = getApiBaseUrl();
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize)
  });
  if (keyword) {
    query.set('keyword', keyword);
  }
  if (hardwareType) {
    query.set('hardware_type', String(hardwareType));
  }
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${baseUrl}/pack-market/list?${query.toString()}`, { headers });
  return response.json();
};

// 兼容旧调用形式：getEmotionMarket(page, pageSize)
export const getEmotionMarketLegacy = async (page = 1, pageSize = 20) => {
  return getEmotionMarket({ page, pageSize });
};

// 获取表情市场详情（公开接口，不需要认证）
export const getEmotionMarketDetail = async (emotionId) => {
  const baseUrl = getApiBaseUrl();
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${baseUrl}/pack-market/${encodeURIComponent(emotionId)}`, { headers });
  return response.json();
};

// 生成图片访问token（公开接口，不需要认证）
export const generateImageAccessToken = async () => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/packs/images/token`, {
    method: 'POST'
  });
  return response.json();
};

// 为图片URL添加token参数
export const addImageToken = (imageUrl, token) => {
  if (!imageUrl || !token) return imageUrl;
  // 如果URL已经包含参数，使用&，否则使用?
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}token=${token}`;
};

// 审核表情包（管理员功能）
export const approveEmotionPackage = async (emotionId, isApproved = true) => {
  return api.put(`/packs/${encodeURIComponent(emotionId)}/approve`, { is_approved: isApproved });
};

// 上架/下架表情包（管理员功能）
export const publishEmotionPackage = async (emotionId, isPublished = true) => {
  return api.put(`/packs/${encodeURIComponent(emotionId)}/publish`, { is_published: isPublished });
};

// ========== 表情包账号授权接口 ==========
// 授权表情包给用户账号
export const grantEmotionToAccount = async (emotionId, userId) => {
  return api.post(`/packs/${encodeURIComponent(emotionId)}/grant-account`, { user_id: userId });
};

// 查询用户账号可用的表情包（包括自己上传的和被授权的）
export const getAccountEmotions = async (userId = null) => {
  const params = userId ? { user_id: userId } : {};
  return api.get('/packs/account-assigned', { params });
};

// 取消用户账号的表情包授权
export const revokeEmotionFromAccount = async (emotionId, userId) => {
  return api.delete(`/packs/${encodeURIComponent(emotionId)}/revoke-account/${userId}`);
};

// 查询表情包的已授权用户列表
export const getGrantedUsers = async (emotionId) => {
  return api.get(`/packs/${encodeURIComponent(emotionId)}/granted-users`);
};

// 获取可用于「设置设备默认」的表情包列表（仅包含支持该设备版型的 zip/bin 包）
export const getPacksForDeviceDefault = async (deviceId) => {
  return api.get('/packs/for-device-default', { params: { device_id: deviceId } });
};

// 设置设备的默认表情包
export const setDeviceDefaultEmotion = async (deviceId, emotionId) => {
  return api.post(`/devices/${deviceId}/default-pack`, { pack_id: emotionId });
};

// 查询设备的默认表情包
export const getDeviceDefaultEmotion = async (deviceId) => {
  return api.get(`/devices/${deviceId}/default-pack`);
};

// 取消设备的默认表情包
export const removeDeviceDefaultEmotion = async (deviceId) => {
  return api.delete(`/devices/${deviceId}/default-pack`);
};
